import { Box, Container, Typography, Stack, Grid, Link, Divider } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import { PAYMENT_BADGES, PayBadge } from "./PaymentBadges";
import { COMPANY } from "../config/companyInfo";
import { ANDROID_APP_URL, IOS_APP_URL } from "../config/appStores";

export default function Footer() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <Box
            component="footer"
            sx={{
                bgcolor: "#1a1a2e",
                color: "white",
                pt: 8,
                pb: 4,
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                            <Logo size={44} light />
                            <Box>
                                <Typography variant="h6" component="div" sx={{ fontWeight: 800 }}>
                                    BusGo
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                    {t("header.tagline")}
                                </Typography>
                            </Box>
                        </Stack>
                        <Typography variant="body2" sx={{ opacity: 0.7, maxWidth: 300, mb: 2 }}>
                            {t("footer.tagline")}
                        </Typography>

                        {/* Bedž stoji samo dok aplikacija postoji na prodavnici —
                            adrese se drže u config/appStores.js, isto kao na početnoj. */}
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            {[
                                {url: ANDROID_APP_URL, icon: "mdi:google-play", label: "Google Play"},
                                {url: IOS_APP_URL, icon: "mdi:apple", label: "App Store"},
                            ].filter((store) => store.url).map((store) => (
                                <Box
                                    key={store.label}
                                    component="a"
                                    href={store.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${t("homePage.downloadOn")} ${store.label}`}
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        border: "1px solid rgba(255,255,255,0.25)",
                                        color: "white",
                                        textDecoration: "none",
                                        transition: "background-color 0.2s, border-color 0.2s",
                                        "&:hover": {
                                            bgcolor: "rgba(255,255,255,0.08)",
                                            borderColor: "rgba(255,255,255,0.5)",
                                        },
                                    }}
                                >
                                    <Icon icon={store.icon} width={26} />
                                    <Stack>
                                        <Typography sx={{ fontSize: "0.6rem", opacity: 0.7, lineHeight: 1 }}>
                                            {t("homePage.downloadOn")}
                                        </Typography>
                                        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.3 }}>
                                            {store.label}
                                        </Typography>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 1 }}>
                            {t("footer.navigation")}
                        </Typography>
                        <Stack spacing={1.5}>
                            <Link onClick={() => navigate("/")} underline="none" sx={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", "&:hover": { color: "white" }, fontSize: "0.875rem" }}>
                                {t("header.home")}
                            </Link>
                            <Link onClick={() => navigate("/moje-karte")} underline="none" sx={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", "&:hover": { color: "white" }, fontSize: "0.875rem" }}>
                                {t("header.myTickets")}
                            </Link>
                            <Link onClick={() => navigate("/login")} underline="none" sx={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", "&:hover": { color: "white" }, fontSize: "0.875rem" }}>
                                {t("header.login")}
                            </Link>
                        </Stack>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 1 }}>
                            {t("footer.help")}
                        </Typography>
                        <Stack spacing={1.5}>
                            {[
                                { label: t("footer.helpCenter"), path: "/pomoc" },
                                { label: t("footer.termsOfUse"), path: "/uslovi-kupovine" },
                                { label: t("footer.privacyPolicy"), path: "/politika-privatnosti" },
                                { label: t("footer.accountDeletion"), path: "/brisanje-naloga" },
                            ].map((item) => (
                                <Link
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    underline="none"
                                    sx={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", "&:hover": { color: "white" }, fontSize: "0.875rem" }}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 700, mb: 2, textTransform: "uppercase", letterSpacing: 1 }}>
                            {t("footer.contact")}
                        </Typography>
                        <Stack spacing={2}>
                            <Link
                                href={`tel:${COMPANY.supportPhoneTel}`}
                                underline="none"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    color: "rgba(255,255,255,0.7)",
                                    "&:hover": { color: "white" },
                                }}
                            >
                                <Icon icon="mdi:phone" width={18} />
                                <Typography variant="body2">{COMPANY.supportPhone}</Typography>
                            </Link>
                            <Link
                                href={`mailto:${COMPANY.supportEmail}`}
                                underline="none"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    color: "rgba(255,255,255,0.7)",
                                    "&:hover": { color: "white" },
                                    wordBreak: "break-all",
                                }}
                            >
                                <Icon icon="mdi:email-outline" width={18} />
                                <Typography variant="body2">{COMPANY.supportEmail}</Typography>
                            </Link>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Icon icon="mdi:map-marker" width={18} style={{ opacity: 0.7, marginTop: 2 }} />
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Mileševska 36A,
                                    <br />11000 Beograd, Srbija
                                </Typography>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 4 }} />

                <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    <Typography variant="caption" sx={{ opacity: 0.5 }}>
                        &copy; {new Date().getFullYear()} BusGo. {t("footer.rightsReserved")}
                    </Typography>
                    <Stack direction="row" spacing={3}>
                        <Typography
                            variant="caption"
                            onClick={() => navigate("/uslovi-kupovine")}
                            sx={{ opacity: 0.5, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                        >
                            {t("footer.termsOfUse")}
                        </Typography>
                        <Typography
                            variant="caption"
                            onClick={() => navigate("/politika-privatnosti")}
                            sx={{ opacity: 0.5, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                        >
                            {t("footer.privacyPolicy")}
                        </Typography>
                    </Stack>
                </Stack>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mt: 4, mb: 3 }} />

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" justifyContent="center">
                        {PAYMENT_BADGES.secure.map((b) => (
                            <PayBadge key={b.file} {...b} />
                        ))}
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                        {PAYMENT_BADGES.processor.map((b) => (
                            <PayBadge key={b.file} {...b} />
                        ))}
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" justifyContent="center">
                        {PAYMENT_BADGES.cards.map((b) => (
                            <PayBadge key={b.file} {...b} />
                        ))}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}
